import { useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { MOCK_CLIENTS, MOCK_VESSELS, ADMIN_USER } from "@/data/portalMockData";
import { Settings, Users, Ship, Shield, Search, Plus, Eye, Download } from "lucide-react";
import { toast } from "sonner";

const moduleToggles = [
  { key: "fleet", label: "Fleet & Vessels", locked: true },
  { key: "voyages", label: "Voyage Operations", locked: true },
  { key: "documents", label: "Documents", locked: true },
  { key: "messages", label: "Messages & Notices", locked: true },
  { key: "hseq", label: "HSEQ & Compliance", locked: false },
  { key: "finance", label: "Commercial & Finance", locked: false },
  { key: "reports", label: "Reports & Analytics", locked: false },
  { key: "satisfaction", label: "Client Satisfaction", locked: false },
  { key: "crew", label: "Crew & Personnel", locked: false },
  { key: "esg", label: "ESG & Emissions", locked: false },
  { key: "training", label: "Training & Competency", locked: false },
  { key: "emergency", label: "Emergency & SAR", locked: false },
  { key: "maintenance", label: "Maintenance & PMS", locked: false },
];

const portalUsers = [
  { name: "John Smith", email: "jsmith@clienta.com", role: "Client Admin", lastLogin: "2025-03-24 14:30", status: "Active" },
  { name: "Sarah Chen", email: "schen@clienta.com", role: "Operations", lastLogin: "2025-03-23 09:15", status: "Active" },
  { name: "Mike Johnson", email: "mjohnson@clientb.com", role: "HSEQ", lastLogin: "2025-03-22 11:00", status: "Active" },
  { name: "Lisa Park", email: "lpark@clientb.com", role: "Finance", lastLogin: "2025-03-20 16:45", status: "Active" },
  { name: "Tom Wilson", email: "twilson@clientc.com", role: "Read Only", lastLogin: "—", status: "Invited" },
];

const voStaff = [
  { name: "Kyle Pemberton", email: "kyle@velocityoffshore.com", role: "Super Admin", dept: "Executive", status: "Active", lastLogin: "2025-03-25 08:00" },
  { name: "Jerome Arnold", email: "jerome@velocityoffshore.com", role: "Admin", dept: "Commercial", status: "Active", lastLogin: "2025-03-25 07:30" },
  { name: "Bo Jardine", email: "bo@velocityoffshore.com", role: "Admin", dept: "Operations", status: "Active", lastLogin: "2025-03-24 18:00" },
  { name: "Eduard Jardine", email: "eduard@velocityoffshore.com", role: "Admin", dept: "Operations", status: "Active", lastLogin: "2025-03-25 09:00" },
];

const auditLog = [
  { timestamp: "2025-03-25 08:01", user: "Kyle Pemberton", tenant: "—", action: "Login", ip: "192.168.1.1", status: "Success" },
  { timestamp: "2025-03-24 16:30", user: "John Smith", tenant: "Client A", action: "Doc Download", ip: "10.0.0.45", status: "Success" },
  { timestamp: "2025-03-24 14:00", user: "Sarah Chen", tenant: "Client A", action: "Voyage Request", ip: "10.0.0.46", status: "Success" },
  { timestamp: "2025-03-24 11:00", user: "Mike Johnson", tenant: "Client B", action: "Invoice Viewed", ip: "172.16.0.12", status: "Success" },
  { timestamp: "2025-03-23 09:00", user: "Kyle Pemberton", tenant: "—", action: "Settings Changed", ip: "192.168.1.1", status: "Success" },
];

const PortalAdmin = () => {
  const [selectedClient, setSelectedClient] = useState(MOCK_CLIENTS[0].id);
  const client = MOCK_CLIENTS.find(c => c.id === selectedClient)!;

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Configuration</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Admin & Settings</h1>
      </div>

      <Tabs defaultValue="clients" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="clients">Client Management</TabsTrigger>
          <TabsTrigger value="vessels">Vessel Management</TabsTrigger>
          <TabsTrigger value="staff">Platform Users</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* Client Management */}
        <TabsContent value="clients">
          <div className="flex gap-4 min-h-[600px]">
            {/* Left - Client list */}
            <div className="w-[240px] portal-card flex flex-col">
              <div className="p-3 border-b border-[hsl(220,15%,90%)]">
                <Input placeholder="Search clients..." className="bg-[hsl(220,20%,97%)] text-xs" />
              </div>
              <div className="flex-1">
                {MOCK_CLIENTS.map(c => (
                  <button key={c.id} onClick={() => setSelectedClient(c.id)} className={`w-full p-3 text-left border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)] ${selectedClient === c.id ? "bg-[hsl(220,20%,97%)]" : ""}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: c.accentColor }}>{c.name.slice(0, 2)}</div>
                      <div>
                        <p className="text-xs font-bold">{c.name}</p>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${c.status === "active" ? "bg-green-100 text-green-700" : c.status === "prospect" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{c.status}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="p-3 border-t border-[hsl(220,15%,90%)]">
                <Button onClick={() => toast.success("Created: Add Client", { description: "New entry added." })} variant="outline" size="sm" className="w-full"><Plus className="w-3.5 h-3.5 mr-1" /> Add Client</Button>
              </div>
            </div>

            {/* Right - Client config */}
            <div className="flex-1 portal-card">
              <Tabs defaultValue="details">
                <div className="px-4 pt-4">
                  <TabsList className="bg-[hsl(220,20%,97%)]">
                    <TabsTrigger value="details">Account</TabsTrigger>
                    <TabsTrigger value="vessels_assign">Vessels</TabsTrigger>
                    <TabsTrigger value="modules">Modules</TabsTrigger>
                    <TabsTrigger value="users">Users</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="details" className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Company Name</label><Input defaultValue={client.name} /></div>
                    <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Status</label>
                      <Select defaultValue={client.status}><SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="prospect">Prospect</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Contract Ref</label><Input defaultValue={client.contractRef} /></div>
                    <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Region</label><Input defaultValue={client.region} /></div>
                    <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Account Manager</label>
                      <Select defaultValue="kyle"><SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="kyle">Kyle Pemberton</SelectItem><SelectItem value="jerome">Jerome Arnold</SelectItem><SelectItem value="bo">Bo Jardine</SelectItem><SelectItem value="eduard">Eduard Jardine</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button className="bg-[hsl(211,100%,40%)] text-white" onClick={() => toast.success("Client updated")}>Save Changes</Button>
                    <Button onClick={() => toast.success("Opening: Preview Portal", { description: "Loading details…" })} variant="outline"><Eye className="w-3.5 h-3.5 mr-1" /> Preview Portal</Button>
                  </div>
                </TabsContent>

                <TabsContent value="vessels_assign" className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-bold mb-2">Assigned to {client.name}</h4>
                      <div className="space-y-1">
                        {MOCK_VESSELS.filter(v => v.client === client.name).map(v => (
                          <div key={v.id} className="flex items-center justify-between p-2 bg-[hsl(220,20%,97%)] rounded-lg">
                            <span className="text-xs">{v.name}</span>
                            <Button onClick={() => toast.success("Removed: Remove", { description: "Action logged in audit trail." })} variant="ghost" size="sm" className="text-[#DC3545] text-[10px] h-6">Remove</Button>
                          </div>
                        ))}
                        {MOCK_VESSELS.filter(v => v.client === client.name).length === 0 && <p className="text-xs text-[hsl(215,12%,50%)] py-4 text-center">No vessels assigned</p>}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold mb-2">Available</h4>
                      <div className="space-y-1">
                        {MOCK_VESSELS.filter(v => v.client !== client.name).map(v => (
                          <div key={v.id} className="flex items-center justify-between p-2 bg-[hsl(220,20%,97%)] rounded-lg">
                            <span className="text-xs">{v.name}</span>
                            <Button onClick={() => toast.success("Acknowledged: Assign", { description: "Signature captured." })} variant="ghost" size="sm" className="text-[#28A745] text-[10px] h-6">Assign</Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="modules" className="p-4">
                  <div className="space-y-3">
                    {moduleToggles.map(m => (
                      <div key={m.key} className="flex items-center justify-between py-2 border-b border-[hsl(220,15%,92%)] last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{m.label}</span>
                          {m.locked && <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">LOCKED ON</span>}
                        </div>
                        <Switch defaultChecked disabled={m.locked} />
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="users" className="p-4">
                  <div className="flex justify-end mb-3">
                    <Button onClick={() => toast.success("Action recorded: Invite User")} variant="outline" size="sm"><Plus className="w-3.5 h-3.5 mr-1" /> Invite User</Button>
                  </div>
                  <table className="w-full text-xs">
                    <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                      {["Name", "Email", "Role", "Last Login", "Status"].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {portalUsers.map((u, i) => (
                        <tr key={i} className="border-b border-[hsl(220,15%,92%)]">
                          <td className="px-3 py-2 font-medium">{u.name}</td>
                          <td className="px-3 py-2 text-[hsl(215,12%,50%)]">{u.email}</td>
                          <td className="px-3 py-2"><span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{u.role}</span></td>
                          <td className="px-3 py-2 text-[hsl(215,12%,50%)]">{u.lastLogin}</td>
                          <td className="px-3 py-2"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${u.status === "Active" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{u.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </TabsContent>

        {/* Vessel Management */}
        <TabsContent value="vessels">
          <div className="portal-card">
            <div className="p-4 flex justify-between border-b border-[hsl(220,15%,90%)]">
              <h2 className="font-bold">Vessel Registry</h2>
              <Button onClick={() => toast.success("Created: Add Vessel", { description: "New entry added." })} variant="outline" size="sm"><Plus className="w-3.5 h-3.5 mr-1" /> Add Vessel</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Name", "Type", "GT", "Flag", "Class", "IMO", "Status", "Client"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {MOCK_VESSELS.map(v => (
                    <tr key={v.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                      <td className="px-4 py-2.5 font-bold">{v.name}</td>
                      <td className="px-4 py-2.5">{v.type}</td>
                      <td className="px-4 py-2.5">{v.grossTonnage}</td>
                      <td className="px-4 py-2.5">{v.flag}</td>
                      <td className="px-4 py-2.5">{v.classSociety}</td>
                      <td className="px-4 py-2.5 font-mono">{v.imoNumber}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${v.status === "on_charter" ? "bg-green-100 text-green-700" : v.status === "standby" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{v.status.replace("_"," ")}</span></td>
                      <td className="px-4 py-2.5">{v.client}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Platform Users */}
        <TabsContent value="staff">
          <div className="portal-card">
            <div className="p-4 flex justify-between border-b border-[hsl(220,15%,90%)]">
              <h2 className="font-bold">VO Staff Users</h2>
              <Button onClick={() => toast.success("Created: Add Staff", { description: "New entry added." })} variant="outline" size="sm"><Plus className="w-3.5 h-3.5 mr-1" /> Add Staff</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Name", "Email", "Role", "Department", "Status", "Last Login"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {voStaff.map((s, i) => (
                    <tr key={i} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-4 py-2.5 font-bold">{s.name}</td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{s.email}</td>
                      <td className="px-4 py-2.5"><span className="text-[9px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{s.role}</span></td>
                      <td className="px-4 py-2.5">{s.dept}</td>
                      <td className="px-4 py-2.5"><span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">{s.status}</span></td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{s.lastLogin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Audit Log */}
        <TabsContent value="audit">
          <div className="portal-card">
            <div className="p-4 flex gap-3 border-b border-[hsl(220,15%,90%)]">
              <Input placeholder="Filter by user..." className="bg-[hsl(220,20%,97%)] max-w-[200px]" />
              <Button onClick={() => toast.success("Generating: Export CSV", { description: "Your file will download shortly." })} variant="outline" size="sm" className="ml-auto"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Timestamp", "User", "Tenant", "Action", "IP", "Status"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {auditLog.map((a, i) => (
                    <tr key={i} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-4 py-2.5 font-mono text-[10px]">{a.timestamp}</td>
                      <td className="px-4 py-2.5 font-medium">{a.user}</td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{a.tenant}</td>
                      <td className="px-4 py-2.5">{a.action}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px] text-[hsl(215,12%,50%)]">{a.ip}</td>
                      <td className="px-4 py-2.5"><span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">{a.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default PortalAdmin;
